import React, { useState, useRef, useCallback } from 'react';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { Modal, Button, Alert } from 'react-bootstrap';
import './ImageCropper.css';

// Target aspect ratio for news cover images (1.82:1 - matches frontend display)
const TARGET_ASPECT_RATIO = 1.82;
const RECOMMENDED_WIDTH = 1024;
const RECOMMENDED_HEIGHT = Math.round(RECOMMENDED_WIDTH / TARGET_ASPECT_RATIO); // ~563px

const ImageCropper = ({
    show,
    onHide,
    imageSrc,
    onCropComplete,
    aspectRatio = TARGET_ASPECT_RATIO,
    title = "Crop Image"
}) => {
    const imgRef = useRef(null);
    const [crop, setCrop] = useState({
        unit: '%',
        width: 90,
        aspect: aspectRatio,
    });
    const [completedCrop, setCompletedCrop] = useState(null);

    const onImageLoad = useCallback((e) => {
        const { width, height } = e.currentTarget;

        // Calculate initial crop to center and maximize area
        const imageAspect = width / height;
        let cropWidth, cropHeight, cropX, cropY;

        if (imageAspect > aspectRatio) {
            // Image is wider than target - fit by height
            cropHeight = 90;
            cropWidth = (cropHeight * aspectRatio * height) / width;
            cropX = (100 - cropWidth) / 2;
            cropY = 5;
        } else {
            // Image is taller than target - fit by width
            cropWidth = 90;
            cropHeight = (cropWidth * width) / (aspectRatio * height);
            cropX = 5;
            cropY = (100 - cropHeight) / 2;
        }

        setCrop({
            unit: '%',
            width: cropWidth,
            height: cropHeight,
            x: cropX,
            y: cropY,
            aspect: aspectRatio,
        });
    }, [aspectRatio]);

    const getCroppedImg = useCallback(() => {
        if (!completedCrop || !imgRef.current) {
            return null;
        }

        const image = imgRef.current;
        const canvas = document.createElement('canvas');
        const scaleX = image.naturalWidth / image.width;
        const scaleY = image.naturalHeight / image.height;

        // Set canvas size to recommended dimensions
        canvas.width = RECOMMENDED_WIDTH;
        canvas.height = RECOMMENDED_HEIGHT;

        const ctx = canvas.getContext('2d');

        ctx.drawImage(
            image,
            completedCrop.x * scaleX,
            completedCrop.y * scaleY,
            completedCrop.width * scaleX,
            completedCrop.height * scaleY,
            0,
            0,
            RECOMMENDED_WIDTH,
            RECOMMENDED_HEIGHT
        );

        return new Promise((resolve) => {
            canvas.toBlob(
                (blob) => {
                    if (!blob) {
                        console.error('Canvas is empty');
                        return;
                    }
                    blob.name = 'cropped-image.jpg';
                    resolve(blob);
                },
                'image/jpeg',
                0.9
            );
        });
    }, [completedCrop]);

    const handleCropConfirm = async () => {
        const croppedBlob = await getCroppedImg();
        if (croppedBlob) {
            // Create a File object from the blob
            const croppedFile = new File([croppedBlob], 'cropped-image.jpg', {
                type: 'image/jpeg',
            });
            onCropComplete(croppedFile);
            onHide();
        }
    };

    return (
        <Modal show={show} onHide={onHide} size="lg" centered fullscreen="md-down">
            <Modal.Header closeButton>
                <Modal.Title>{title}</Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-2 p-md-3">
                <Alert variant="info" className="mb-3">
                    <Alert.Heading className="h6">Image Guidelines</Alert.Heading>
                    <ul className="mb-0 small">
                        <li><strong>Recommended:</strong> {RECOMMENDED_WIDTH} x {RECOMMENDED_HEIGHT}px</li>
                        <li><strong>Aspect ratio:</strong> {aspectRatio.toFixed(2)}:1</li>
                        <li>Drag to reposition the crop area</li>
                    </ul>
                </Alert>

                <div className="crop-container">
                    {imageSrc && (
                        <ReactCrop
                            crop={crop}
                            onChange={(c) => setCrop(c)}
                            onComplete={(c) => setCompletedCrop(c)}
                            aspect={aspectRatio}
                            className="crop-image"
                        >
                            <img
                                ref={imgRef}
                                src={imageSrc}
                                alt="Crop preview"
                                onLoad={onImageLoad}
                                style={{ maxHeight: '50vh', maxWidth: '100%' }}
                            />
                        </ReactCrop>
                    )}
                </div>

                {completedCrop && (
                    <div className="mt-2">
                        <small className="text-muted">
                            Output: {RECOMMENDED_WIDTH} x {RECOMMENDED_HEIGHT}px
                        </small>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer className="d-flex gap-2">
                <Button
                    variant="secondary"
                    onClick={onHide}
                    className="flex-grow-1 flex-md-grow-0"
                    style={{ minHeight: '44px' }}
                >
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    onClick={handleCropConfirm}
                    className="flex-grow-1 flex-md-grow-0"
                    style={{ minHeight: '44px' }}
                >
                    Apply Crop
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ImageCropper;

// Export constants for use in other components
export { TARGET_ASPECT_RATIO, RECOMMENDED_WIDTH, RECOMMENDED_HEIGHT };
